import { useState } from 'react';
import type { Friendship } from '../../types';
import { useApp } from '../../context/AppContext';
import { getAirline } from '../../utils/airlines';
import { hapticMedium, haptic } from '../../utils/haptics';
import { Check, X, Loader2 } from 'lucide-react';
import { AvatarImg } from '../shared/Avatar';

interface PendingRequestProps {
  friendship: Friendship;
}

export function PendingRequest({ friendship }: PendingRequestProps) {
  const { acceptFriendRequest, rejectFriendRequest, usersById } = useApp();
  const [status, setStatus] = useState<'idle' | 'accepting' | 'accepted'>('idle');
  const fromUser = usersById.get(friendship.fromUserId);
  if (!fromUser) return null;

  const airline = getAirline(fromUser.airlineCode);

  const handleAccept = async () => {
    setStatus('accepting');
    try {
      await acceptFriendRequest(friendship.id);
      hapticMedium();
      setStatus('accepted');
    } catch (err) {
      console.warn('[PendingRequest] accept failed:', err);
      setStatus('idle');
    }
  };

  return (
    <div className={`flex items-center gap-3 bg-slate-800 rounded-xl p-3 border transition-all ${
      status === 'accepted' ? 'border-sage/50 bg-sage/5' : 'border-slate-700/50'
    }`}>
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 overflow-hidden"
        style={{ backgroundColor: airline.color, color: airline.textOnColor }}
      >
        <AvatarImg src={fromUser.avatarUrl} name={fromUser.name} />
      </div>
      <div className="flex-1">
        <span className="text-sm font-medium text-slate-100">{fromUser.name}</span>
        <span className="text-xs text-slate-500 ml-1">@{fromUser.handle}</span>
      </div>
      {status === 'accepted' ? (
        <span className="text-xs font-medium text-sage">Accepted</span>
      ) : (
        <>
          <button
            onClick={handleAccept}
            disabled={status === 'accepting'}
            className="p-1.5 rounded-full bg-sage/15 text-sage hover:bg-sage/25 disabled:opacity-50"
          >
            {status === 'accepting' ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
          </button>
          <button
            onClick={() => { haptic(); rejectFriendRequest(friendship.id).catch((err) => console.warn('[PendingRequest] reject failed:', err)); }}
            disabled={status === 'accepting'}
            className="p-1.5 rounded-full bg-red-500/15 text-red-400 hover:bg-red-500/25 disabled:opacity-50"
          >
            <X size={16} />
          </button>
        </>
      )}
    </div>
  );
}
